import React from 'react'
import * as R from 'remeda'

import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import { SortButton } from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { useLogEvent } from '~/hooks/useLogEvent'
import { exists } from '~/utils'

import { useDisplayRowItems } from '../hooks/useDisplayRowItems'
import { useExpandText } from '../hooks/useExpandText'
import { useGroupSortItems } from '../hooks/useGroupSortItems'
import { useIndividualTableColumns } from '../hooks/useIndividualTableColumns'
import { useIndividualTableInfo } from '../hooks/useIndividualTableInfo'
import { useSerializeGroupTable } from '../hooks/useSerializeGroupTable'
import { useTabulatedGroups } from '../hooks/useTabulatedGroups'
import {
  useAtomContext,
  useCommentContext,
  useSavedCommentContext,
  useSetAtomContext,
} from '../providers/AtomsProvider'
import { useCommentGenerationContext } from '../providers/CommentGenerationProvider'
import { useFigureInfo } from '../providers/CreateFigureInfoProvider'
import { useRestoringParams } from '../providers/CreateRestoringParamsProvider'
import { type DisplayRowId, type RowData } from '../utils'

import { EditGroupsDiffButton } from './EditGroupsDiffButton'
import styles from './IndividualTable.css'

export type IndividualTableProps<T extends RowData<string>> = {
  getAnswerText: (rowData: T) => string
  getLabel: (rowsData: T[]) => string
  getRowId: (rowData: T) => string
  getSubLabel: (rowData: T) => string
  id: string
  rowsData: T[]
}

export const IndividualTable = <T extends RowData<string>>({
  getAnswerText,
  getLabel,
  getRowId,
  getSubLabel,
  id,
  rowsData,
}: IndividualTableProps<T>) => {
  const sortButtonItems = useGroupSortItems()

  const [sortValue, setSortValue] = useAtomContext('individualSortValue')

  const displayRowItems = useDisplayRowItems('individual', [rowsData], {
    getLabel,
    getRowId,
    getSubLabel,
  })

  const setHiddenRowIds = useSetAtomContext('hiddenRowIds')
  const handleHiddenRowChange = React.useCallback(
    (hiddenIds: DisplayRowId[]) => {
      setHiddenRowIds((prevHiddenIds) =>
        R.concat(
          R.difference(
            prevHiddenIds,
            displayRowItems.flatMap(({ subItems }) =>
              subItems.map(R.prop('id')),
            ),
          ),
          hiddenIds,
        ),
      )
    },
    [displayRowItems, setHiddenRowIds],
  )

  const columns = useIndividualTableColumns()

  const figureInfo = useFigureInfo('individual_questionnaire')

  const restoreKey = `individual-table-${id}`
  const restoringParams = useRestoringParams(restoreKey)

  const tableInfo = useIndividualTableInfo(rowsData, {
    getAnswerText,
    getRowId,
    styles: { answerText: styles.cellAnswerTextColumn },
  })

  const { handleTextExpandedChange, isTextExpanded, textLineCount } =
    useExpandText(tableInfo)

  const image = (
    <Table
      columns={columns}
      tableInfo={tableInfo}
      textLineCount={textLineCount}
    />
  )

  const tabulatedGroups = useTabulatedGroups()

  const { generate, isGenerating } = useGenerateSingleComment()
  const { outputType, surveyId } = useCommentGenerationContext()
  const serializeGroupTable = useSerializeGroupTable()

  const [comment, setComment] = useCommentContext(restoreKey)
  const [savedComment, setSavedComment] = useSavedCommentContext(restoreKey)

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: '',
      filterDescription: '',
      filterPatternIndex: 0,
      outputType,
      segmentMode: 'none',
      surveyId,
      tabulationData: serializeGroupTable(columns, tableInfo),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    generate,
    logEvent,
    outputType,
    serializeGroupTable,
    surveyId,
    columns,
    tableInfo,
    setComment,
  ])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              isTextExpanded={isTextExpanded}
              isVisibleExpandTextCheckbox={true}
              rowItems={displayRowItems}
              onHiddenRowChange={handleHiddenRowChange}
              onTextExpandedChange={handleTextExpandedChange}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />

            {1 <= tabulatedGroups.length && <EditGroupsDiffButton />}
          </>
        }
      >
        <Table
          columns={columns}
          stickyColumns={1}
          tableInfo={tableInfo}
          textLineCount={textLineCount}
        />
      </TableContainer>
    </>
  )
}
